import { useState } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Button } from "@/shared/components/button";
import { PillTabs } from "@/shared/components/PillTabs";
import { EmptyState } from "@/shared/components/EmptyState";
import { formatRelativeTime } from "@/shared/lib/formatRelativeTime";
import { toastError } from "@/shared/lib/toast";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import { useQuery } from "@tanstack/react-query";
import type { QueueItem } from "@/portals/clinician/api/reviewQueueApi";
import { queueQuery as sharedQueueQuery, useClaimItem } from "@/portals/clinician/api/clinicianQueries";
import { MINE_EMPTY, MyItemsTable, useMyReviewItems } from "./MyItemsTable";
import { MedicineNameCell } from "./reviewBadges";

export const CLAIM_CONFLICT_MESSAGE = "Another clinician already claimed this item.";
export const QUEUE_EMPTY = "No items waiting in the shared queue.";
export { MINE_EMPTY };

type TabId = "queue" | "mine";

interface ClinicianDashboardViewProps {
  onOpenItem: (id: string) => void;
  /** View a shared-queue item read-only, before claiming it. */
  onPreviewItem?: (id: string) => void;
}

export function ClinicianDashboardView({ onOpenItem, onPreviewItem }: ClinicianDashboardViewProps) {
  const [tab, setTab] = useState<TabId>("queue");

  // From the shared cache: coming back from an item shows both lists at once,
  // then revalidates quietly; socket events keep them live (useClinicianLiveQueries).
  const queueQuery = useQuery(sharedQueueQuery());
  const mineQuery = useMyReviewItems();
  const queue = queueQuery.data ?? [];
  const mine = mineQuery.data ?? [];

  // The row leaves the queue at once; it comes back if the claim is refused.
  const claimMutation = useClaimItem();
  const claimingId = claimMutation.isPending ? claimMutation.variables : null;
  const handleClaim = (item: QueueItem) => {
    claimMutation.mutate(item.id, {
      onSuccess: (data) => onOpenItem(data?.id ?? item.id),
      onError: (caught) => {
        const error = caught as { code?: string; message?: string };
        toastError(error.code === "ITEM_ALREADY_CLAIMED" ? CLAIM_CONFLICT_MESSAGE : error.code === "REVIEW_ITEM_NOT_FOUND" ? "This item isn't available." : error.message ?? "Unable to claim this item.");
      },
    });
  };

  const queueColumns: ColumnsType<QueueItem> = [
    { title: "Issue", key: "issue", render: (_, i) => <span className="font-semibold text-[#0A2947]">{ISSUE_LABELS[i.triggerType]}</span> },
    { title: "Medicine", key: "medicineName", render: (_, i) => <MedicineNameCell name={i.medicineName} triggerType={i.triggerType} /> },
    { title: "Records", dataIndex: "recordCount", key: "recordCount", width: 90 },
    { title: "Waiting since", key: "createdAt", render: (_, i) => formatRelativeTime(i.createdAt) },
    {
      title: "",
      key: "claim",
      align: "right",
      // Pinned, so Claim stays in view when a narrow screen scrolls the table.
      fixed: "right",
      render: (_, i) => (
        <div className="flex items-center justify-end gap-2">
          {onPreviewItem && (
            <Button
              variant="outline"
              onClick={() => onPreviewItem(i.id)}
              className="!h-8 !rounded-lg !border-[#E5E7EB] !px-4 !text-xs !font-semibold !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]"
            >
              View
            </Button>
          )}
          <Button
            busy={claimingId === i.id}
            disabled={claimingId !== null && claimingId !== i.id}
            onClick={() => handleClaim(i)}
            className="!h-8 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !text-xs !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
          >
            Claim
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="rounded-3xl border border-[#E5E7EB] bg-white p-3 shadow-sm sm:p-5">
        <div className="mb-5 flex justify-center">
          <PillTabs
            label="Review queue view"
            active={tab}
            onChange={setTab}
            tabs={[
              { id: "queue", label: `Shared queue (${queue.length})` },
              { id: "mine", label: `My items (${mine.length})` },
            ]}
          />
        </div>

        {tab === "queue" ? (
          <Table<QueueItem>
            rowKey="id"
            columns={queueColumns}
            dataSource={queue}
            loading={queueQuery.isPending}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            locale={{ emptyText: <EmptyState variant="compact" title={QUEUE_EMPTY} /> }}
            scroll={{ x: 640 }}
          />
        ) : (
          <MyItemsTable items={mine} loading={mineQuery.isPending} onOpenItem={onOpenItem} />
        )}
      </div>
    </div>
  );
}
